import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";

import { useUpdateFileMutation } from "@dashboard/files/api";
import { FileStatus } from "@dashboard/files/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { FileSettingsDialog } from "@dashboard/shared/components/FileSettingsDialog";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { createRolloutTag } from "@dashboard/shared/tags";
import { useVisibilityOptions } from "@dashboard/shared/useVisibilityOptions";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { flexRender, Row } from "@tanstack/react-table";

import ActionsMenu from "../ActionsMenu/ActionsMenu";
import { FileExtendedTableItem } from "../UploadTable/UploadTable.utils";

import { useStyles } from "./TableItem.style";

type ActionCellProps = {
    row: Row<FileExtendedTableItem>;
};
const ActionCell = (props: ActionCellProps) => {
    const [modalOpen, setModalOpen] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);

    const isUploading = props.row.original.status === FileStatus.Pending;

    const isExpired = useCurrentSubscriptionExpired();
    const { data: workspace } = useCurrentWorkspace();
    const { fileAbility } = PermissionService();

    const mayManageTag = fileAbility(props.row.original).can("manage", "Tag");

    const getTagBtnTooltip = () => {
        const result = { title: t("pages:filesPage.tooltips.manageTags") };

        if (!mayManageTag) {
            result.title = t("pages:filesPage.tooltips.noTagPermission");
        }

        // An expired subscription also revokes "manage Tag", so it has to win over the role
        // message - otherwise the tooltip blames the user's role for a billing state.
        if (isExpired) {
            result.title = t("pages:filesPage.tooltips.subscriptionExpired");
        }
        return result;
    };

    if (!workspace) {
        return null;
    }

    return (
        <MinimalTableCell align="right">
            <div className={classes.buttonBox}>
                <SecondaryButton
                    tooltipProps={getTagBtnTooltip()}
                    disabled={isUploading || !mayManageTag}
                    onClick={() => setModalOpen(true)}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>
                <ActionsMenu file={props.row.original} />
            </div>
            <FileSettingsDialog open={modalOpen} onClose={() => setModalOpen(false)} item={props.row.original} />
        </MinimalTableCell>
    );
};

type FileUploadTableItemProps = {
    row: Row<FileExtendedTableItem>;
    style?: React.CSSProperties;
};
const TableItem = (props: FileUploadTableItemProps) => {
    const { classes } = useStyles();
    const item = props.row.original;
    const isUploading = item.status === FileStatus.Pending;

    return (
        <HoverRow {...props.row} clickable={false} className={isUploading ? classes.isUploading : undefined}>
            {props.row.getVisibleCells().map((cell) =>
                flexRender(cell.column.columnDef.cell, {
                    ...cell.getContext(),
                    style: props.style,
                    key: cell.id,
                }),
            )}
        </HoverRow>
    );
};

type Props = {
    row: Row<FileExtendedTableItem>;
    style?: React.CSSProperties;
};
const VisibilityChangeCell = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const { data: workspace } = useCurrentWorkspace();
    const currentWorkspaceGroups = useSelector(selectGroups);
    const { groupAbility } = PermissionService();

    const [updateFile] = useUpdateFileMutation();

    const fileItem = props.row.original;
    const currentGroup = currentWorkspaceGroups.find((item) => item.id === fileItem.groupId);
    const mayManageFile = groupAbility(currentGroup || null, workspace).can("manage", "File");
    const visibilityOptions = useVisibilityOptions();

    const getSelectTooltip = () => {
        const result = { title: t("pages:filesPage.tooltips.changeVisibility") };

        if (!mayManageFile) {
            result.title = t("pages:filesPage.tooltips.noVisibilityPermission");
        }
        return result;
    };

    const handleAccessibilityChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        const value = selectedOption?.value;

        const isGroupId = value !== workspace?.id;

        if (typeof value === "number" && !Number.isNaN(value) && workspace) {
            try {
                await updateFile({
                    id: fileItem.id,
                    groupId: isGroupId ? value : null,
                    workspaceId: isGroupId ? null : value,
                });

                showSuccessToast(t("pages:filesPage.toasts.fileVisibilityUpdated"));
            } catch (error) {
                const err = error as Error;
                showErrorToast(err.message);
            }
        }
    };

    if (!workspace) {
        return null;
    }
    return (
        <Box style={props.style}>
            <Tooltip {...getSelectTooltip()}>
                <SelectField
                    value={visibilityOptions.find((option) => option.value === (currentGroup?.id || workspace.id))}
                    options={visibilityOptions}
                    className={classes.visibilitySelect}
                    fullWidth
                    onChange={handleAccessibilityChange}
                    disabled={!mayManageFile || fileItem.status === FileStatus.Pending}
                    disableSorting
                />
            </Tooltip>
        </Box>
    );
};

const TagsCell = ({ row }: Props) => {
    const { classes } = useStyles();

    const item = row.original;
    const removableTags = item.metadata?.user?.tags;
    const rolloutTag = createRolloutTag(item.rolloutPercentage);
    const hasTags = removableTags?.length || rolloutTag;

    return (
        <MinimalTableCell>
            <div className={classes.tagsBox}>
                {removableTags?.length ? removableTags.map((t) => <Tag key={t.key} item={t} />) : null}
                {rolloutTag ? <Tag key={rolloutTag.key} item={rolloutTag} theme="light" /> : null}
                {!hasTags ? <Typography>-</Typography> : null}
            </div>
        </MinimalTableCell>
    );
};

export { ActionCell, TableItem, TagsCell, VisibilityChangeCell };
